import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";

const useAttachmentHandlers = ({ ticketStatus }) => {
  const [medias, setMedias] = useState([]);
  const [mediaCaptions, setMediaCaptions] = useState([]);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const mediaCaptionInputRef = useRef(null);

  const validateFileSize = (file) => {
    const whatsappLimits = {
      audio: 16 * 1024 * 1024,
      video: 16 * 1024 * 1024,
      image: 5 * 1024 * 1024,
      document: 100 * 1024 * 1024,
    };

    const fileType = file.type.split("/")[0];
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const limitMB = (whatsappLimits[fileType] / (1024 * 1024)).toFixed(0);

    if (file.size > whatsappLimits[fileType]) {
      const errorMessage =
        `⚠️ ARCHIVO DEMASIADO GRANDE\n\n` +
        `📁 Archivo: ${file.name}\n` +
        `📊 Tamaño: ${fileSizeMB}MB\n` +
        `🚫 Límite WhatsApp: ${limitMB}MB\n\n` +
        `💡 SOLUCIONES:\n` +
        `• Comprimir el archivo a menos de ${limitMB}MB\n` +
        `• Usar un servicio de almacenamiento (Google Drive, Dropbox)\n` +
        `• Dividir el archivo en partes más pequeñas\n\n` +
        `ℹ️ Los límites de WhatsApp son:\n` +
        `• Audio: 16MB máximo\n` +
        `• Video: 16MB máximo\n` +
        `• Imagen: 5MB máximo\n` +
        `• Documento: 100MB máximo`;

      toast.error(errorMessage, {
        toastId: `file-too-large-${file.name}`,
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        style: {
          backgroundColor: "#ff4444",
          color: "#ffffff",
          fontSize: "14px",
          fontWeight: "bold",
          maxWidth: "400px",
          whiteSpace: "pre-line",
        },
      });

      return false;
    }

    return true;
  };

  const getFileType = (file) => {
    if (file.type.startsWith("image/"))
      return <span role="img" aria-label="imagen">🖼️</span>;
    if (file.type.startsWith("video/"))
      return <span role="img" aria-label="video">🎥</span>;
    if (file.type.startsWith("audio/"))
      return <span role="img" aria-label="audio">🎵</span>;
    if (file.type.includes("pdf"))
      return <span role="img" aria-label="documento pdf">📄</span>;
    if (file.type.includes("document") || file.type.includes("word"))
      return <span role="img" aria-label="documento">📝</span>;
    if (file.type.includes("sheet") || file.type.includes("excel"))
      return <span role="img" aria-label="hoja de cálculo">📊</span>;
    return <span role="img" aria-label="archivo">📎</span>;
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) return;

    const selectedMedias = Array.from(e.target.files);
    const validMedias = [];

    for (const media of selectedMedias) {
      if (validateFileSize(media)) {
        validMedias.push(media);
      }
    }

    if (validMedias.length > 0) {
      setMedias((prevMedias) => {
        const newMedias = [...prevMedias, ...validMedias];
        setActiveMediaIndex(newMedias.length - 1);
        return newMedias;
      });
      setMediaCaptions((prev) => [...prev, ...validMedias.map(() => "")]);
    }
  };

  const handleInputPaste = (e) => {
    const items = e.clipboardData.items;
    if (!items) return;

    let fileToAdd = null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (file && validateFileSize(file)) {
          fileToAdd = file;
          break;
        }
      }
    }
    if (!fileToAdd) return;

    if (typeof e.preventDefault === "function") e.preventDefault();
    if (typeof e.stopPropagation === "function") e.stopPropagation();

    setMedias((prevMedias) => {
      const newMedias = [...prevMedias, fileToAdd];
      setActiveMediaIndex(newMedias.length - 1);
      return newMedias;
    });
    setMediaCaptions((prev) => [...prev, ""]);
  };

  const handleMediaCaptionPaste = (e) => {
    if (ticketStatus !== "open") return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        e.preventDefault();
        e.stopPropagation();
        handleInputPaste(e);
        return;
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = [];

    for (const file of files) {
      if (validateFileSize(file)) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      setMedias((prevMedias) => {
        const newMedias = [...prevMedias, ...validFiles];
        setActiveMediaIndex(newMedias.length - 1);
        return newMedias;
      });
      setMediaCaptions((prev) => [
        ...prev,
        ...validFiles.map(() => ""),
      ]);
    }
  };

  const handleRemoveMedia = (indexToRemove) => {
    setMedias((prevMedias) => {
      const newMedias = prevMedias.filter((_, index) => index !== indexToRemove);

      if (newMedias.length === 0) {
        setActiveMediaIndex(0);
      } else if (activeMediaIndex >= newMedias.length) {
        setActiveMediaIndex(newMedias.length - 1);
      } else if (activeMediaIndex > indexToRemove) {
        setActiveMediaIndex(activeMediaIndex - 1);
      }

      return newMedias;
    });
    setMediaCaptions((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleSelectActiveMedia = (index) => {
    if (index >= 0 && index < medias.length) {
      setActiveMediaIndex(index);
      setTimeout(() => mediaCaptionInputRef.current?.focus?.(), 0);
    }
  };

  const addFiles = (files) => {
    const validFiles = [];
    for (const file of files) {
      if (validateFileSize(file)) validFiles.push(file);
    }
    if (validFiles.length > 0) {
      setMedias((prev) => {
        const newMedias = [...prev, ...validFiles];
        setActiveMediaIndex(newMedias.length - 1);
        return newMedias;
      });
      setMediaCaptions((prev) => [...prev, ...validFiles.map(() => "")]);
    }
  };

  const handleCancelAll = () => {
    setMedias([]);
    setMediaCaptions([]);
    setActiveMediaIndex(0);
    setUploadProgress({});
  };

  const resetAttachments = () => {
    setMedias([]);
    setMediaCaptions([]);
    setActiveMediaIndex(0);
    setUploadProgress({});
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && medias.length > 0) {
        handleCancelAll();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medias.length]);

  const prevMediasLenCaptionRef = useRef(0);
  useEffect(() => {
    if (medias.length === 0) {
      prevMediasLenCaptionRef.current = 0;
      return;
    }
    const prev = prevMediasLenCaptionRef.current;
    prevMediasLenCaptionRef.current = medias.length;
    if (prev === 0) {
      const tid = setTimeout(() => {
        mediaCaptionInputRef.current?.focus?.();
      }, 120);
      return () => clearTimeout(tid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medias.length]);

  return {
    medias,
    setMedias,
    mediaCaptions,
    setMediaCaptions,
    activeMediaIndex,
    setActiveMediaIndex,
    isDragOver,
    uploadProgress,
    setUploadProgress,
    mediaCaptionInputRef,
    handleChangeMedias,
    handleInputPaste,
    handleMediaCaptionPaste,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    addFiles,
    handleRemoveMedia,
    handleSelectActiveMedia,
    handleCancelAll,
    validateFileSize,
    getFileType,
    resetAttachments,
  };
};

export default useAttachmentHandlers;
